import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  AlertTriangle,
  Loader2,
  Sparkles,
  Wand2,
  CheckCircle2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

/**
 * Bulk promotion card — coup de balai legacy `validated` → `talent_pool`.
 *
 * 2 modes :
 *   - "all"   : promeut TOUS les `validated`.
 *   - "smart" : ne promeut que les profils récents (<90j) avec CV + email valide.
 *
 * Garde-fous :
 *   - Préview obligatoire avant action (counts).
 *   - Double confirmation (preview → confirmation explicite).
 *   - Rapport détaillé après exécution.
 */
const BulkPromoteCard = ({ onComplete }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmMode, setConfirmMode] = useState(null); // null | "all" | "smart"
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/talent-pool/bulk-promote/preview');
      const data = await res.json();
      setPreview(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const runPromotion = async () => {
    if (!confirmMode) return;
    setRunning(true);
    try {
      const res = await api.post('/admin/talent-pool/bulk-promote', { mode: confirmMode });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setReport(data);
      toast.success(`${data.promoted} candidat(s) promus en Talent Pool`);
      // Refresh preview counts
      await loadPreview();
      if (onComplete) onComplete(data);
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setRunning(false);
      setConfirmMode(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-4 md:p-5 border-orange-200 bg-orange-50/50" data-testid="bulk-promote-card-loading">
        <div className="flex items-center gap-2 text-orange-700">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Analyse du stock legacy...</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-4 md:p-5 border-red-200 bg-red-50/50">
        <p className="text-sm text-red-700">Erreur preview : {error}</p>
      </Card>
    );
  }

  // Nothing to do
  if (!preview || preview.current_validated === 0) {
    return null;
  }

  const allCount = preview.all_eligible || 0;
  const smartCount = preview.smart_eligible || 0;
  const skipped = preview.smart_skipped || {};

  return (
    <>
      <Card
        className="p-4 md:p-5 border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50/40"
        data-testid="bulk-promote-card"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
            <Wand2 className="h-4 w-4 text-orange-700" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-orange-900">
              Coup de balai legacy
            </h3>
            <p className="text-xs text-orange-800/80 mt-0.5">
              <span className="font-bold">{preview.current_validated}</span> candidat(s) actuellement bloqué(s) en statut{' '}
              <code className="px-1 py-0.5 bg-white/70 rounded text-[11px]">validated</code>.
              Promeus-les vers <code className="px-1 py-0.5 bg-white/70 rounded text-[11px]">talent_pool</code> pour qu'ils apparaissent dans ton vivier.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Mode SMART */}
          <button
            type="button"
            onClick={() => setConfirmMode('smart')}
            disabled={smartCount === 0}
            className="text-left rounded-md bg-white border-2 border-emerald-300 hover:border-emerald-500 p-3 disabled:opacity-50 disabled:cursor-not-allowed transition"
            data-testid="bulk-promote-smart-btn"
          >
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-4 w-4 text-emerald-700" />
              <span className="text-sm font-semibold text-emerald-900">Promotion intelligente</span>
            </div>
            <div className="text-2xl font-bold text-emerald-700">{smartCount}</div>
            <p className="text-[11px] text-gray-600 mt-1 leading-snug">
              Récents (&lt;90j) + CV présent + email valide.
            </p>
            <div className="text-[10px] text-gray-500 mt-1.5 space-y-0.5">
              <div>• Sans CV : <span className="font-medium">{skipped.no_cv || 0}</span></div>
              <div>• Email invalide : <span className="font-medium">{skipped.invalid_email || 0}</span></div>
              <div>• Inactifs &gt;90j : <span className="font-medium">{skipped.old_inactive_90d || 0}</span></div>
            </div>
          </button>

          {/* Mode ALL */}
          <button
            type="button"
            onClick={() => setConfirmMode('all')}
            disabled={allCount === 0}
            className="text-left rounded-md bg-white border-2 border-orange-300 hover:border-orange-500 p-3 disabled:opacity-50 disabled:cursor-not-allowed transition"
            data-testid="bulk-promote-all-btn"
          >
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-4 w-4 text-orange-700" />
              <span className="text-sm font-semibold text-orange-900">Promouvoir tout</span>
            </div>
            <div className="text-2xl font-bold text-orange-700">{allCount}</div>
            <p className="text-[11px] text-gray-600 mt-1 leading-snug">
              Tous les <code className="text-[10px]">validated</code>, sans filtre. Risque de pollution.
            </p>
            <p className="text-[10px] text-orange-700 mt-1.5 italic">
              ⚠ Ne transforme pas la poubelle en KPI.
            </p>
          </button>
        </div>

        <p className="text-[10px] text-gray-500 mt-3">
          Déjà dans le Talent Pool : <span className="font-medium">{preview.already_in_talent_pool}</span>
        </p>
      </Card>

      {/* CONFIRMATION DIALOG */}
      <Dialog open={!!confirmMode} onOpenChange={(open) => !open && setConfirmMode(null)}>
        <DialogContent data-testid="bulk-promote-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmMode === 'smart' ? (
                <><Sparkles className="h-5 w-5 text-emerald-600" /> Promotion intelligente</>
              ) : (
                <><AlertTriangle className="h-5 w-5 text-orange-600" /> Promouvoir tout</>
              )}
            </DialogTitle>
            <DialogDescription>
              {confirmMode === 'smart' ? (
                <>
                  <span className="text-base font-bold text-emerald-700">{smartCount} candidat(s)</span> seront promus en Talent Pool.
                  Les autres ({preview.current_validated - smartCount}) resteront en <code>validated</code>.
                </>
              ) : (
                <>
                  <span className="text-base font-bold text-orange-700">{allCount} candidat(s)</span> seront promus en Talent Pool, <strong>sans filtre qualité</strong>.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="bg-gray-50 rounded-md p-3 text-xs space-y-1 text-gray-700">
            <p>• Les <code>rejected</code>, <code>archived</code>, <code>rejected_auto</code> ne sont jamais touchés.</p>
            <p>• Action loguée dans les logs admin (<code>[BULK_PROMOTE]</code>).</p>
            <p>• Réversible candidat par candidat via le bouton "Rejeter" sur la fiche.</p>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmMode(null)} disabled={running} data-testid="bulk-promote-cancel-btn">
              <X className="h-4 w-4 mr-2" />
              Annuler
            </Button>
            <Button
              onClick={runPromotion}
              disabled={running}
              className={confirmMode === 'smart' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-orange-600 hover:bg-orange-700 text-white'}
              data-testid="bulk-promote-execute-btn"
            >
              {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              {running ? 'Promotion en cours...' : 'Confirmer la promotion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REPORT DIALOG */}
      <Dialog open={!!report} onOpenChange={(open) => !open && setReport(null)}>
        <DialogContent data-testid="bulk-promote-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Promotion terminée
            </DialogTitle>
            <DialogDescription>Mode : <code className="text-xs">{report?.mode}</code></DialogDescription>
          </DialogHeader>

          {report && (
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-md border border-emerald-200">
                <span className="text-sm font-medium text-emerald-900">Promus en Talent Pool</span>
                <span className="text-xl font-bold text-emerald-700">{report.promoted}</span>
              </div>
              {report.skipped_no_cv > 0 && (
                <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-md text-sm">
                  <span className="text-gray-700">Ignorés — sans CV</span>
                  <span className="font-medium">{report.skipped_no_cv}</span>
                </div>
              )}
              {report.skipped_invalid_email > 0 && (
                <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-md text-sm">
                  <span className="text-gray-700">Ignorés — email invalide</span>
                  <span className="font-medium">{report.skipped_invalid_email}</span>
                </div>
              )}
              {report.skipped_old > 0 && (
                <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-md text-sm">
                  <span className="text-gray-700">Ignorés — inactifs &gt;90j</span>
                  <span className="font-medium">{report.skipped_old}</span>
                </div>
              )}
              {report.skipped_excluded > 0 && (
                <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-md text-sm">
                  <span className="text-gray-700">Ignorés — rejetés/archivés</span>
                  <span className="font-medium">{report.skipped_excluded}</span>
                </div>
              )}
              {report.errors > 0 && (
                <div className="flex items-center justify-between p-2.5 bg-red-50 rounded-md text-sm border border-red-200">
                  <span className="text-red-700">Erreurs</span>
                  <span className="font-medium text-red-700">{report.errors}</span>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setReport(null)} data-testid="bulk-promote-report-close-btn">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkPromoteCard;
